'use client';

import {
    type BoxProps,
    Center,
    chakra,
    createSlotRecipeContext,
    Flex,
    type HTMLChakraProps,
    Icon,
    IconButton,
    type StackProps,
    Text,
    useBreakpointValue,
    VStack,
} from '@chakra-ui/react';
import { useAnimate, useReducedMotion } from 'motion/react';
import {
    Children,
    createContext,
    forwardRef,
    type ReactNode,
    type RefObject,
    startTransition,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { IconType } from 'react-icons';
import {
    BsLayoutSidebarInset,
    BsLayoutSidebarInsetReverse,
} from 'react-icons/bs';
import { LuChevronsUpDown } from 'react-icons/lu';
import { Link, type LinkProps, NavLink } from 'react-router';
import { AccountAvatar } from './account-avatar';
import { BrandLogo } from './brand-logo';
import { hoverScrollbarStyles } from './scrollbar';
import {
    SCROLLED_SHADOW,
    SCROLLED_SHADOW_UP,
    SHADOW_CLIP_ABOVE,
    SHADOW_CLIP_BELOW,
} from './shell-scroll';
import { SHELL_NAVBAR_END_VAR, ShellTexture } from './shell-texture';
import { sidebarRecipe } from './sidebar.recipe';
import { Tooltip } from './tooltip';

const { withProvider, withContext } = createSlotRecipeContext({
    recipe: sidebarRecipe,
});

// Collapse is a width-only animation, to the icons-only mini rail:
// 32px-square item pills on the hosts' regular 16px insets — the same box
// as the rail's other 32px icon controls. Icon wells are 32px in BOTH
// states, so icons keep one x through the whole animation. ONE model at
// every breakpoint — there is no separate mobile overlay.
const COLLAPSED_WIDTH = '64px';
// One knob for the whole collapse: root/panel width, well width, title
// collapse, label/logo fades and the hosts' inset transitions all derive
// from it (crank it up for slow-motion debugging).
const COLLAPSE_DURATION = 0.25; // s
const COLLAPSE_EASE: [number, number, number, number] = [0.4, 0, 0.2, 1];
const COLLAPSE_EASE_CSS = 'cubic-bezier(0.4, 0, 0.2, 1)';
// Text fades are asymmetric on the collapse clock: OUT runs through the
// front half — finished just before the clip edge sweeps the label boxes
// (a full-length fade gets cropped mid-run and reads as vanishing) — and
// IN starts a quarter in, easing the text back as the rail reopens.
const FADE_OUT = `opacity ${COLLAPSE_DURATION * 0.5}s ease`;
const FADE_IN = `opacity ${COLLAPSE_DURATION * 0.6}s ease ${COLLAPSE_DURATION * 0.25}s`;
// The collapse's VISUAL state lives on the root as data-collapsed, set at
// the click (the same frame the width animation starts) — every style that
// rides the collapse clock keys off this selector, not React state, so the
// deferred re-render of the rail's consumers can't desync them. Per-state
// styles too: a style prop keyed to React state mints a new class on the
// first toggle, and its generation + insertion stalls the animation.
const COLLAPSED = '[data-collapsed] &';
// Set on the root only while a toggle animates (SidebarRoot): transitions
// keyed to it fire for the collapse alone — never on navigation, and never
// under reduced motion (the toggle then applies instantly).
const TOGGLING = '[data-toggling] &';

type NavProps = HTMLChakraProps<'nav'>;
type DivProps = HTMLChakraProps<'div'>;

// A <nav>, not <aside>: this is the app's primary navigation landmark,
// labelled in SidebarRoot below.
const SidebarRootEl = withProvider<HTMLElement, NavProps>('nav', 'root');
const SidebarPanelEl = withContext<HTMLDivElement, DivProps>('div', 'panel');
const SidebarGroupEl = withContext<HTMLDivElement, DivProps>('div', 'group');
const SidebarItemEl = withContext<HTMLDivElement, DivProps>('div', 'item');
const SidebarFooterEl = withContext<HTMLDivElement, DivProps>('div', 'footer');

/** Block pinned below the sidebar's scrollable nav area — compose it as the
 * scroll container's NEXT SIBLING. While the nav has more below (flagged on
 * the panel by Sidebar.Nav) it shows its top rule (the recipe) and casts
 * the scroll shadow upward. */
function SidebarFooter({ css, ...props }: DivProps) {
    return (
        <SidebarFooterEl
            // Above the nav (a previous sibling) so its shadow falls on it.
            zIndex={1}
            transition='box-shadow 0.15s ease'
            // Merged, not overridden: callers pass their own css.
            css={[FOOTER_SCROLLED_CSS, css]}
            {...props}
        />
    );
}

// ── Shared sidebar chrome ────────────────────────────────────────────────
// The pieces every app's sidebar column composes identically: brand band,
// scrollable nav, pinned footer control row.

/** The muted-ink ghost scheme every chrome icon control shares. */
export const chromeControlStyles = {
    color: '{colors.fg.muted}',
    _hover: { bg: '{colors.bg.hover}', color: '{colors.fg}' },
    _active: { bg: '{colors.bg.active}' },
} as const;

/** Popup placement for controls living on the rail: regular placement when
 * expanded, flown out past the rail's right edge in mini (the 24px gutter
 * covers the 16px trigger inset plus breathing room). */
function useSidebarFlyout(expanded: 'top-start' | 'bottom-start'): {
    placement: 'top-start' | 'bottom-start' | 'right-end';
    gutter?: number;
} {
    return useSidebarMini()
        ? { placement: 'right-end', gutter: 24 }
        : { placement: expanded };
}

// The nav's scroll state, flagged on the panel by Sidebar.Nav: content
// above (the brand band casts the top bar's shadow down) and content below
// (the footer casts it up and shows its rule — the recipe).
const NAV_SCROLLED_ATTR = 'data-nav-scrolled';
const NAV_MORE_BELOW_ATTR = 'data-nav-more-below';
const BRAND_SCROLLED_CSS = {
    [`[${NAV_SCROLLED_ATTR}] > &`]: { boxShadow: SCROLLED_SHADOW },
    clipPath: SHADOW_CLIP_BELOW,
};
// The pinned switcher's width (see SidebarBrand): its content, never
// narrower than the expanded rail leaves it (from the root's
// --sidebar-width). Capped there expanded; collapsed only by the top bar's
// trailing controls — the dock is fixed, outside the bar's flow; name and
// crumb truncate instead, its end rule landing on the first control's. The
// cap eases between the two (a name wider than the expanded slot grows and
// shrinks smoothly); the width itself has no transition of its own: the
// crumb's eased width drives it, so the two can't drift apart and squeeze
// the name mid-collapse (the eased cap stays ahead of the crumb). Both
// rules always drawn — the start one over the mini rail's border, the end
// one over the rail's own edge.
const DOCK_WIDTH = `calc(var(--sidebar-width) - ${COLLAPSED_WIDTH} + 1px)`;
const DOCK_MAX = `calc(100% - ${COLLAPSED_WIDTH} + 2px - var(${SHELL_NAVBAR_END_VAR}, 0px))`;
const SWITCHER_DOCK = {
    width: 'fit-content',
    minWidth: `min(${DOCK_WIDTH}, ${DOCK_MAX})`,
    maxWidth: DOCK_WIDTH,
    borderInlineWidth: '1px',
    // Eased only where the crumb's width eases too (interpolate-size):
    // elsewhere the crumb snaps open, and an easing cap would squeeze the
    // name behind it — so the cap snaps with it.
    '@supports (interpolate-size: allow-keywords)': {
        [TOGGLING]: {
            transition: `max-width ${COLLAPSE_DURATION}s ${COLLAPSE_EASE_CSS}`,
        },
    },
    [COLLAPSED]: { maxWidth: DOCK_MAX },
};

const FOOTER_SCROLLED_CSS = {
    [`[${NAV_MORE_BELOW_ATTR}] > &`]: { boxShadow: SCROLLED_SHADOW_UP },
    clipPath: SHADOW_CLIP_ABOVE,
};

// The nav's scrollbar (Sidebar.Nav): quiet expanded, shown only while the
// rail is hovered (no painted track rectangle); the 64px rail has zero
// slack for even that — a classic scrollbar would crush the 32px pills —
// so mini hides it entirely. Wheel/drag still scroll.
const NAV_SCROLLBAR_CSS = {
    ...hoverScrollbarStyles,
    [COLLAPSED]: {
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
    },
};

/** The brand band at the rail's top: the IonQ logo (BrandLogo — the mark
 * swaps per theme). Only the letters fade with the rail — the mark holds
 * still (the rail clips the faded letters' box). `wordmark=false` drops the
 * letters altogether (the band shows the mark alone) and the band's
 * `children` — e.g. the context switcher row — sit beside the mark, staying
 * put over the top bar in the mini rail. */
function SidebarBrand({
    wordmark = true,
    children,
}: {
    /** False shows the mark alone. */
    wordmark?: boolean;
    /** Content beside the mark (with `wordmark={false}`): fills the band's
     * remaining width, pinned over the top bar in the mini rail. */
    children?: ReactNode;
}) {
    return (
        <>
            {/* Own clip: in the mini rail the faded wordmark still occupies
                its box inside the home link — un-clipped it would overflow
                the rail, invisible but hit-testable over the top bar. */}
            <Flex
                align='center'
                // The shell's top bar height: the band's rule meets the bar's.
                height='var(--shell-navbar-height, 64px)'
                flexShrink={0}
                overflow='hidden'
                // The clip stops 1px short of the panel's edge — the rail's
                // border, which the panel overlaps — so nothing in the band
                // paints over it.
                me='1px'
                // The band's rule meets the top bar's (same height, both
                // border-box) — one line across rail and content column.
                borderBottomWidth='1px'
                borderColor='border'
                // Stacking context for the shared top-band texture under it;
                // above the nav (a later sibling) so its shadow falls on it.
                position='relative'
                isolation='isolate'
                zIndex={1}
                transition='box-shadow 0.15s ease'
                css={BRAND_SCROLLED_CSS}
            >
                <ShellTexture offset={0} />
                {/* The whole mark cell is the home link, highlighted like the
                switcher beside it: full height, out to the rule (the
                mark's 18.3px inset mirrored as end padding). */}
                <ContextLink
                    to='/'
                    aria-label='IONQ home'
                    display='flex'
                    alignItems='center'
                    alignSelf='stretch'
                    flexShrink={0}
                    pe='18px'
                    // Alone in the band (no switcher beside it), the cell is the
                    // whole band.
                    flex={children ? undefined : 1}
                    // Solid on the rail's ground: the band's texture stops at
                    // the mark's cell. Its hover lands on the top bar cells'
                    // (the wash over the bar's bg.page), not on the rail's.
                    bg='bg.canvas'
                    transition='background 0.2s ease'
                    _hover={{
                        bg: 'color-mix(in srgb, {colors.fg} 6%, {colors.bg.page})',
                    }}
                    _focusVisible={FOCUS_RING}
                >
                    {/* The 32px-tall mark (~27.7px wide), 18.3px in, centres
                    on the rail (x=32) in BOTH states, so it holds still
                    through the collapse. Mark-only, the box is pinned to
                    46px (inset + mark): the cell spans the mini rail. */}
                    <chakra.div
                        ps='18.3px'
                        width={wordmark ? undefined : '46px'}
                        overflow={wordmark ? undefined : 'hidden'}
                        css={{
                            '& [data-wordmark]': {
                                opacity: 1,
                                transition: FADE_IN,
                                [COLLAPSED]: {
                                    opacity: 0,
                                    transition: FADE_OUT,
                                },
                            },
                        }}
                    >
                        <BrandLogo wordmark={wordmark} />
                    </chakra.div>
                </ContextLink>
            </Flex>
            {children && (
                // ONE switcher for both rail states: pinned to the top band
                // right of the mini rail's 64px edge (fixed — it outlives the
                // rail's collapse clip), filling out to the rail's edge while
                // expanded and hugging its content over the top bar while
                // collapsed, easing between the two on the collapse clock. The
                // top bar renders no copy of its own.
                <chakra.div
                    // Hook for page-only captures (the e2e visual suites).
                    data-sidebar-dock=''
                    position='fixed'
                    top={0}
                    // On the mini rail's border: one line, not two.
                    left={`calc(${COLLAPSED_WIDTH} - 1px)`}
                    h='calc(var(--shell-navbar-height, 64px) - 1px)'
                    display='flex'
                    zIndex={1}
                    borderColor='border'
                    css={SWITCHER_DOCK}
                >
                    {children}
                </chakra.div>
            )}
        </>
    );
}

/** The scrollable middle of the sidebar column. Groups carry their own
 * bottom margins (a stack gap would also pad the scroll edges), and the
 * faded labels deliberately overhang the mini rail — overflow-x pins to
 * hidden so that never grows a horizontal bar. */
function SidebarNav({ children, ...props }: StackProps) {
    const ref = useRef<HTMLDivElement>(null);
    // The scrollbar's gutter as --sidebar-gutter, which the groups take out
    // of their right inset: pills keep one width whether or not the bar has
    // room (0 for overlay scrollbars and the barless mini rail).
    useLayoutEffect(() => {
        const scroller = ref.current;
        const inner = scroller?.firstElementChild;
        if (!scroller || !(inner instanceof HTMLElement)) return;
        // Writes only on change: the observer fires every frame of the
        // collapse animation, and an unconditional write re-dirties the
        // nav's style each time, so the next read forces a full sync layout.
        let gutter = '';
        const measure = () => {
            const next = `${scroller.offsetWidth - scroller.clientWidth}px`;
            if (next === gutter) return;
            gutter = next;
            scroller.style.setProperty('--sidebar-gutter', next);
        };
        // The scroll state on the panel (attributes only — see
        // NAV_SCROLLED_ATTR & co.).
        const panel = scroller.parentElement;
        const sync = () => {
            const { scrollTop, scrollHeight, clientHeight } = scroller;
            panel?.toggleAttribute(NAV_SCROLLED_ATTR, scrollTop > 0);
            panel?.toggleAttribute(
                NAV_MORE_BELOW_ATTR,
                scrollTop + clientHeight < scrollHeight - 1,
            );
        };
        measure();
        sync();
        // The gutter appearing narrows the inner column, so watching it
        // catches overflow flips as well as rail resizes (and content
        // growth: groups mounted later) — the mini rail's bar swap too.
        const observer = new ResizeObserver(() => {
            measure();
            sync();
        });
        observer.observe(scroller);
        observer.observe(inner);
        scroller.addEventListener('scroll', sync, { passive: true });
        return () => {
            observer.disconnect();
            scroller.removeEventListener('scroll', sync);
        };
    }, []);
    return (
        <chakra.div
            ref={ref}
            flex='1'
            minHeight={0}
            // Breathing room under the band's rule — the same 16px the
            // pages keep under the top bar.
            pt={4}
            overflowY='auto'
            overflowX='hidden'
            css={NAV_SCROLLBAR_CSS}
            {...props}
        >
            {/* Inner column: the footer's overflow observer watches this box
                so content growth (late-mounted groups included) re-checks. */}
            <VStack align='stretch' gap={0}>
                {children}
            </VStack>
        </chakra.div>
    );
}

type SidebarContextRowProps = {
    /** The current context's name (org, partner…). */
    name: string;
    /** The context's tile (logo, initials fallback from `name`) ahead of
     * the name; absent, text only. */
    avatar?: { logoUrl?: string | null };
    /** The enclosing context: a link crumb ahead of the switcher —
     * `parent > [switcher]` — sliding in while the rail is collapsed (zero-
     * wide and inert while expanded); not rendered at all below md
     * (`Sidebar.useContextTrail()`). Absent, the switcher renders alone (a
     * root context). */
    parent?: { name: string; to: LinkProps['to']; tooltip?: string };
    /** The app's switcher menu with `Sidebar.ContextTrigger` as its
     * trigger (it renders this row's name/avatar). */
    children: ReactNode;
};

// A router link the chakra way — style props straight on the anchor.
const ContextLink = chakra(Link);

/** Whether the bar has room for the context row's parent crumb (md+). One
 * JS answer for the row AND its host's menu: below it the crumb folds away
 * and whatever the crumb offered (the way up a level) must be back in the
 * menu. */
function useSidebarContextTrail(): boolean {
    return (
        useBreakpointValue({ base: false, md: true }, { ssr: false }) ?? true
    );
}

// The light feedback (both themes): a 6% ink wash, translucent so the
// textures show through.
const WASH = 'color-mix(in srgb, {colors.fg} 6%, transparent)';

const FOCUS_RING = {
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: 'colorPalette.focusRing',
    outlineOffset: '-2px',
} as const;

/** The top bar's trailing controls (bell, account menu) as flat cells:
 * full height, ruled off on their left, the row's light wash under the
 * pointer or while open — the same language as the context switcher's
 * segment leading the bar. */
export const chromeSegmentStyles = {
    alignSelf: 'stretch',
    h: 'auto',
    rounded: 0,
    borderWidth: 0,
    borderInlineStartWidth: '1px',
    borderColor: '{colors.border}',
    bg: 'transparent',
    transition: 'background 0.2s ease, color 0.2s ease',
    _hover: { bg: WASH },
    _expanded: { bg: WASH },
    _focusVisible: FOCUS_RING,
} as const;

// The row's tooltips (the switcher's purpose, the crumb's destination).
const ROW_TOOLTIP = {
    positioning: { placement: 'bottom' },
    showArrow: true,
    openDelay: 200,
    closeDelay: 0,
    lazyMount: true,
    unmountOnExit: true,
} as const;

// The breadcrumb's chevron: how far its point reaches past the crumb's
// body; the crumb ends (and the switcher starts) that far plus the
// segments' 12px text inset from it; the matching arrow / notch masks.
const CHEVRON_DEPTH = 12;
const CHEVRON_INSET = `${CHEVRON_DEPTH + 12}px`;
const CRUMB_ARROW = `polygon(0 0, calc(100% - ${CHEVRON_DEPTH}px) 0, 100% 50%, calc(100% - ${CHEVRON_DEPTH}px) 100%, 0 100%)`;
const SWITCHER_NOTCH = `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${CHEVRON_DEPTH}px 50%)`;
// The notch's flat twin (same point count, so the two interpolate).
const SWITCHER_FLAT = 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 50%)';
const TRAIL_EASE = `${COLLAPSE_DURATION}s ${COLLAPSE_EASE_CSS}`;
// The crumb shows only over the top bar (the collapsed rail): it slides
// in from the left there, pushing the switcher right.
const TRAIL_SHOWN = '[data-collapsed] [data-breadcrumb] &';

const ContextRowContext = createContext<
    Pick<SidebarContextRowProps, 'name' | 'avatar'> | undefined
>(undefined);

function useContextRow() {
    const context = useContext(ContextRowContext);
    if (!context) {
        throw new Error('Sidebar.ContextTrigger needs a ContextRow');
    }
    return context;
}

// The switcher's own box: a flat segment filling its row's height (the
// pinned dock owns the placement — SidebarBrand, SWITCHER_DOCK). After a
// crumb, it starts with the notch freeing the crumb's arrow point.
const SWITCHER_FACE = {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: 2,
    minW: 0,
    ps: 4,
    pe: 4,
    color: '{colors.fg}',
    textAlign: 'start',
    clipPath: SWITCHER_FLAT,
    [TOGGLING]: {
        transition: `background 0.2s ease, clip-path ${TRAIL_EASE}, padding ${TRAIL_EASE}`,
    },
    [TRAIL_SHOWN]: { clipPath: SWITCHER_NOTCH, ps: CHEVRON_INSET },
} as const;

const SWITCHER_FEEDBACK = {
    cursor: 'pointer',
    transition: 'background 0.2s ease',
    '&:hover, &[aria-expanded=true]': { bg: WASH },
    _focusVisible: FOCUS_RING,
} as const;

/** The row's face: tile (where given) and name. */
function ContextFace() {
    const { name, avatar } = useContextRow();
    return (
        <>
            {/* 2xs (24px): the org's tile. */}
            {avatar && (
                <AccountAvatar
                    name={name}
                    logoUrl={avatar.logoUrl}
                    size='2xs'
                />
            )}
            {/* The nav items' size, one weight up so the current context
                doesn't read as the list's first item. */}
            <Text
                data-sentry-mask
                flex='1'
                minW={0}
                fontSize='sm'
                fontWeight='medium'
                lineHeight={1.2}
                truncate
            >
                {name}
            </Text>
        </>
    );
}

type SidebarContextTriggerProps = React.ComponentProps<typeof chakra.button> & {
    /** The control's purpose — its tooltip, and its accessible name after
     * the visible one ("Acme, switch organization"). */
    label?: string;
};

/** The whole context row as ONE button: the switcher menu's trigger (a
 * real button forwarding props and ref, so it mounts via Menu.Trigger
 * asChild), the up-down chevrons marking it a selector. */
const SidebarContextTrigger = forwardRef<
    HTMLButtonElement,
    SidebarContextTriggerProps
>(function SidebarContextTrigger(
    { label = 'Switch organization', ...props },
    ref,
) {
    const { name } = useContextRow();
    return (
        <Tooltip
            content={label}
            {...ROW_TOOLTIP}
            // Menu.Trigger asChild hands the button ITS id (child props win
            // the asChild merge) — point the tooltip at that one.
            ids={{ trigger: props.id }}
        >
            <chakra.button
                ref={ref}
                type='button'
                // Visible name first, purpose after — voice control matches
                // what is on screen.
                aria-label={`${name}, ${label.toLowerCase()}`}
                css={[SWITCHER_FACE, SWITCHER_FEEDBACK]}
                {...props}
            >
                <ContextFace />
                <Icon
                    as={LuChevronsUpDown}
                    aria-hidden
                    boxSize={4}
                    flexShrink={0}
                    // 16px clear of the name (8px gap + 8px).
                    ms={2}
                    color='{colors.fg.muted}'
                />
            </chakra.button>
        </Tooltip>
    );
});

/** The context row — sidebar-07's team switcher slot: the current context
 * as one switcher (Sidebar.ContextTrigger inside the app's menu), flat and
 * full height in the rail band's pinned slot (Sidebar.Brand). With a
 * `parent` the parent's crumb leads while collapsed, the two segments split
 * by a chevron:
 * the crumb's highlight is an arrow ending ON it, the switcher's starts
 * with the matching notch (SWITCHER_FACE). */
function SidebarContextRow({
    name,
    avatar,
    parent,
    children,
}: SidebarContextRowProps) {
    const trail = useSidebarContextTrail() && parent;
    const mini = useSidebarMini();
    return (
        <Flex
            data-breadcrumb={trail ? '' : undefined}
            align='stretch'
            alignSelf='stretch'
            flex={1}
            minW={0}
            maxW='100%'
        >
            {trail && (
                <>
                    {/* Zero-wide beside the expanded rail's band; eases open
                        when collapsed, anchored at its end so the crumb
                        slides in from the left. */}
                    <chakra.div
                        display='flex'
                        justifyContent='flex-end'
                        flexShrink={0}
                        overflow='hidden'
                        inert={!mini || undefined}
                        css={{
                            width: 0,
                            interpolateSize: 'allow-keywords',
                            [TOGGLING]: { transition: `width ${TRAIL_EASE}` },
                            [TRAIL_SHOWN]: { width: 'auto' },
                        }}
                    >
                        <ContextCrumb {...trail} />
                    </chakra.div>
                    {/* The '>' rule, overlaid on the segments' shared edge. */}
                    <chakra.svg
                        aria-hidden
                        viewBox={`0 0 ${CHEVRON_DEPTH} 100`}
                        preserveAspectRatio='none'
                        flexShrink={0}
                        w={`${CHEVRON_DEPTH}px`}
                        ms={`-${CHEVRON_DEPTH}px`}
                        position='relative'
                        zIndex={1}
                        pointerEvents='none'
                        color='{colors.border}'
                        // Zero net width while hidden; shown, it also
                        // overlaps the switcher (its notch).
                        css={{
                            opacity: 0,
                            marginInlineEnd: 0,
                            [TOGGLING]: {
                                transition: `opacity ${TRAIL_EASE}, margin ${TRAIL_EASE}`,
                            },
                            [TRAIL_SHOWN]: {
                                opacity: 1,
                                marginInlineEnd: `-${CHEVRON_DEPTH}px`,
                            },
                        }}
                    >
                        <polyline
                            points={`0,0 ${CHEVRON_DEPTH},50 0,100`}
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={1}
                            vectorEffect='non-scaling-stroke'
                        />
                    </chakra.svg>
                </>
            )}
            <ContextRowContext value={{ name, avatar }}>
                {children}
            </ContextRowContext>
        </Flex>
    );
}

/** The parent's crumb: one level up, so one ink step down — muted at rest
 * (the rail's section-label grey), shown whole; full ink and the wash under
 * the pointer, its highlight an arrow pointing at the switcher. */
function ContextCrumb({
    name,
    to,
    tooltip,
}: NonNullable<SidebarContextRowProps['parent']>) {
    const link = (
        <ContextLink
            to={to}
            display='flex'
            alignItems='center'
            flexShrink={0}
            // Long partner names truncate, keeping the switcher in view.
            maxW='16rem'
            ps={4}
            pe={CHEVRON_INSET}
            color='{colors.fg.muted}'
            textDecoration='none'
            clipPath={CRUMB_ARROW}
            transition='background 0.2s ease, color 0.2s ease'
            _hover={{ bg: WASH, color: '{colors.fg}' }}
            _focusVisible={FOCUS_RING}
        >
            <Text data-sentry-mask fontSize='sm' lineHeight={1.2} truncate>
                {name}
            </Text>
        </ContextLink>
    );
    if (!tooltip) return link;
    return (
        <Tooltip content={tooltip} {...ROW_TOOLTIP}>
            {link}
        </Tooltip>
    );
}

/** The pinned footer control row: `children` lead from the start edge,
 * `end` — the rail toggle unless overridden — is pushed to the far edge.
 * The collapsing width itself folds the row into a single column via
 * container queries whose thresholds derive from the control count — the
 * mini rail stacks them, `end` at the bottom. */
function SidebarFooterControls({
    children,
    end = <SidebarToggleButton />,
}: {
    children: ReactNode;
    /** The control at the far edge, after the spacer (default: the rail
     * toggle; null for none). */
    end?: ReactNode;
}) {
    const controls = Children.toArray(children);
    const count = controls.length + (end ? 1 : 0);
    // 32px per control, no gaps, 32px horizontal padding; -1 so the queries
    // engage strictly below the exact-fit width.
    const foldQuery = `@container (max-width: ${32 * count + 31}px)`;
    const stackQuery = `@container (max-width: ${32 * (count - 1) + 31}px)`;
    const stack = { [stackQuery]: { flexBasis: '100%' } };
    return (
        <SidebarFooter css={{ containerType: 'inline-size' }}>
            <Flex wrap='wrap' align='center' px={4} py='10px'>
                {controls.map((child, index) => (
                    // Static control list — order is identity.
                    <chakra.div key={index} css={stack}>
                        {child}
                    </chakra.div>
                ))}
                {end && (
                    <chakra.div
                        ms='auto'
                        css={{
                            ...stack,
                            // Folded: no spacer left to push against.
                            [foldQuery]: { marginInlineStart: 0 },
                        }}
                    >
                        {end}
                    </chakra.div>
                )}
            </Flex>
        </SidebarFooter>
    );
}

// Persisted open/closed choice. Storage access is guarded: it can
// throw when site data is blocked, and then the choice simply doesn't
// survive a reload.
const SIDEBAR_STORAGE_KEY = 'sidebar_open';

// With no stored choice, the rail starts open from the md breakpoint up
// (Chakra's default 48rem) and collapsed below it: an expanded 256px rail
// would leave a phone's page a ~120px column.
const SIDEBAR_DEFAULT_OPEN_QUERY = '(min-width: 48rem)';

function readStoredOpen(): boolean {
    try {
        const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
        if (stored !== null) return stored !== 'false';
    } catch {
        // Storage unavailable: fall through to the viewport default.
    }
    return typeof window === 'undefined'
        ? true
        : window.matchMedia(SIDEBAR_DEFAULT_OPEN_QUERY).matches;
}

function storeOpen(open: boolean) {
    try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(open));
    } catch {
        // Not persisted, but the in-memory state still works.
    }
}

type SidebarContextType = {
    isSidebarOpen: boolean;
    toggleSidebar: () => void;
    /** Set by the Sidebar root: applies a new open state's VISUALS
     * (data-collapsed + the width animation) imperatively, ahead of the
     * React render. */
    animatorRef: RefObject<((open: boolean) => void) | null>;
};

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

// ONE rail model at every breakpoint: an in-flow column that toggles
// between the expanded width and the icons-only mini rail. (The former
// mobile-only fixed overlay — force-collapse, close-on-navigation, scrim —
// is gone; small screens just get the same collapsible rail.)
function SidebarProvider({ children }: { children: ReactNode }) {
    // The open/closed choice, persisted across visits. Defaults to open.
    const [isSidebarOpen, setIsSidebarOpen] = useState(readStoredOpen);
    // The latest REQUESTED state: rapid toggles must never resolve against a
    // stale render's value (the render below is deferred).
    const openRef = useRef(isSidebarOpen);
    const animatorRef = useRef<((open: boolean) => void) | null>(null);

    useEffect(() => {
        storeOpen(isSidebarOpen);
    }, [isSidebarOpen]);

    const value = useMemo(
        () => ({
            isSidebarOpen,
            // Everything visual starts NOW, in one frame (data-collapsed
            // flips every collapse-clock style; the width animation starts);
            // the re-render of the rail's consumers (tooltips, inert, the
            // context row's crumb — ~60ms) runs as a transition, time-sliced
            // between animation frames instead of blocking the first one.
            toggleSidebar: () => {
                const next = !openRef.current;
                openRef.current = next;
                animatorRef.current?.(next);
                startTransition(() => setIsSidebarOpen(next));
            },
            animatorRef,
        }),
        [isSidebarOpen],
    );

    return <SidebarContext value={value}>{children}</SidebarContext>;
}

function useSidebar() {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
}

type SidebarProps = {
    children?: ReactNode;
    width?: string;
} & BoxProps;

const RAIL_CSS = {
    '--sidebar-rail-hit': '12px',
    '@media (pointer: coarse)': { display: 'none' },
    cursor: 'w-resize',
    [COLLAPSED]: { cursor: 'e-resize' },
};

/** sidebar-07's edge rail, rendered by every Sidebar root: an invisible
 * 16px strip straddling the rail's right border, full height, that toggles
 * the sidebar on click. Hovering paints a 2px brand-accent line on the
 * border and shows
 * a resize cursor pointing the way the edge will move (collapse when open,
 * expand when mini). tabIndex -1: a pointer affordance only — the keyboard
 * path is the footer's toggle (Sidebar.ToggleButton). Half the strip
 * hangs outside the rail, so the root must be un-clipped (insetRailProps). */
function SidebarRail() {
    const { toggleSidebar } = useSidebar();
    return (
        <chakra.div
            // Pointer-only duplicate of the footer's toggle: a plain div
            // (a button — even at tabIndex -1 — takes focus on click and
            // would park it on an element hidden from AT).
            aria-hidden
            title='Toggle sidebar'
            onClick={toggleSidebar}
            position='absolute'
            insetY={0}
            // Straddles the root's 1px right border, biased outward: 4px
            // inside (clear of the nav's 8px scrollbar thumb), 8px over
            // the content column. Pointer-only — touch has the footer's
            // toggle, and a full-height edge strip is an accidental-tap
            // hazard there.
            right='-8px'
            w='var(--sidebar-rail-hit)'
            css={RAIL_CSS}
            zIndex={20}
            // The root disables pointer events (its animated box overhangs
            // the content); re-enable them on the strip itself.
            pointerEvents='auto'
            bg='transparent'
            border='none'
            p={0}
            _after={{
                content: '""',
                position: 'absolute',
                insetY: 0,
                left: '50%',
                w: '2px',
                transform: 'translateX(-50%)',
                bg: 'transparent',
                transition: 'background-color 0.2s ease',
            }}
            // The brand accent (the tab underline's orange): the edge reads
            // as interactive, not as a heavier border.
            _hover={{ _after: { bg: '{colors.brand.accent}' } }}
        />
    );
}

function SidebarRoot({ children, width, css, ...props }: SidebarProps) {
    const { isSidebarOpen, animatorRef } = useSidebar();
    const [scope, animate] = useAnimate();
    const panelRef = useRef<HTMLDivElement>(null);
    const shouldReduceMotion = useReducedMotion();

    // Last open state applied (data-collapsed + width).
    const lastOpen = useRef<boolean | null>(null);
    // The latest open state the toggle's imperative start animated to,
    // pending until React's (deferred) render catches up with it.
    const started = useRef<boolean | null>(null);
    const apply = useRef<(open: boolean, instant: boolean) => void>(() => {});
    const toggling = useRef<ReturnType<typeof setTimeout>>(undefined);
    apply.current = (open, instant) => {
        lastOpen.current = open;
        scope.current?.toggleAttribute('data-collapsed', !open);
        // Marks the toggle's run (TOGGLING) for transitions that must not
        // fire on other changes.
        if (!instant) {
            const el = scope.current;
            el?.setAttribute('data-toggling', '');
            clearTimeout(toggling.current);
            toggling.current = setTimeout(
                () => el?.removeAttribute('data-toggling'),
                COLLAPSE_DURATION * 1000 + 50,
            );
        }
        const target = open ? (width ?? '') : COLLAPSED_WIDTH;
        const transition = instant
            ? ({ duration: 0 } as const)
            : ({ duration: COLLAPSE_DURATION, ease: COLLAPSE_EASE } as const);
        animate(scope.current, { width: target }, transition);
        // The panel rides the same clock, so content REFLOWS with the
        // moving edge instead of snapping to the final layout at toggle
        // time. Endpoints are the literal widths — laid out independently
        // of the root's 1px border, which would otherwise shave the grid to
        // 63px (see the panel's width below).
        if (panelRef.current) {
            animate(panelRef.current, { width: target }, transition);
        }
    };
    useLayoutEffect(() => {
        animatorRef.current = (open) => {
            started.current = open;
            apply.current(open, Boolean(shouldReduceMotion));
        };
        return () => {
            animatorRef.current = null;
        };
    }, [animatorRef, shouldReduceMotion]);
    useLayoutEffect(() => {
        // A toggle's imperative start is in flight: React is catching up
        // (possibly through intermediate states of rapid toggles) — leave
        // the running animation alone; clear once it reaches the latest.
        // ONLY that skips: mount (and StrictMode's re-run, whose simulated
        // unmount stops useAnimate's pending animations) always (re)apply.
        if (started.current !== null) {
            if (started.current === isSidebarOpen) started.current = null;
            return;
        }
        const openChanged =
            lastOpen.current !== null && lastOpen.current !== isSidebarOpen;
        // Reduced-motion users get a snap. Runs that don't flip the open
        // state also snap — in particular the mount reconciliation: a
        // sidebar persisted closed must start closed, not play a closing
        // animation over the first paint of every page load. (Gating on
        // "first run" instead breaks under StrictMode: the mount effect
        // re-runs with refs preserved, and the re-run would animate.)
        apply.current(
            isSidebarOpen,
            Boolean(shouldReduceMotion) || !openChanged,
        );
    }, [isSidebarOpen, shouldReduceMotion]);

    return (
        <SidebarRootEl
            ref={scope}
            // First paint's collapse state (see COLLAPSED); toggles flip it
            // imperatively before React catches up with the same value.
            data-collapsed={isSidebarOpen ? undefined : ''}
            // Merged, not overridden: callers pass their own css.
            css={[
                {
                    // The expanded width, for the pinned switcher
                    // (SidebarBrand).
                    '--sidebar-width': width,
                    // First-paint width per state (the app's width is the
                    // expanded one); the effects animate the inline width
                    // after.
                    width,
                    '&[data-collapsed]': { width: COLLAPSED_WIDTH },
                },
                css,
            ]}
            aria-label='Primary'
            position='relative'
            height='100%'
            overflow='hidden'
            pointerEvents='none'
            {...props}
        >
            <SidebarPanelEl
                ref={panelRef}
                // No own clipping: the NAV is the scroll container (and
                // pins the fading labels with its overflow-x).
                height='100%'
                // First-paint width per state; the layout effect above
                // animates the inline width afterwards. An explicit width
                // (not the root's content box) keeps the layout math off the
                // root's 1px border, which would otherwise shave the grid to
                // 63px and knock every 32px control off-grid.
                css={{ width, [COLLAPSED]: { width: COLLAPSED_WIDTH } }}
                pointerEvents='auto'
            >
                {children}
            </SidebarPanelEl>
            <SidebarRail />
        </SidebarRootEl>
    );
}

SidebarRoot.displayName = 'Sidebar';

// Hoisted collapse styles (see COLLAPSED). Fades: out on the collapse's
// front half, in delayed (FADE_OUT / FADE_IN).
const FADE_WITH_COLLAPSE = {
    opacity: 1,
    transition: FADE_IN,
    [COLLAPSED]: { opacity: 0, transition: FADE_OUT },
};
const GROUP_COLLAPSE = { [COLLAPSED]: { mb: 0 } };
const TITLE_COLLAPSE_TRANSITION = `max-height ${COLLAPSE_DURATION}s ${COLLAPSE_EASE_CSS}, margin-bottom ${COLLAPSE_DURATION}s ${COLLAPSE_EASE_CSS}`;
const TITLE_COLLAPSE = {
    [COLLAPSED]: {
        opacity: 0,
        maxH: '0px',
        mb: 0,
        transition: `${FADE_OUT}, ${TITLE_COLLAPSE_TRANSITION}`,
    },
};

interface SidebarItemGroupProps extends StackProps {
    title?: string;
    children: ReactNode;
}

function SidebarItemGroup({
    title,
    children,
    ...props
}: SidebarItemGroupProps) {
    return (
        <VStack
            align='stretch'
            gap={0}
            ps={4}
            // The 16px inset, less the nav's scrollbar gutter.
            pe='calc(16px - var(--sidebar-gutter, 0px))'
            // Section rhythm rides per-group bottom margins (a stack gap on
            // the scroll column would also pad its edges); the mini rail
            // collapses it on the width clock so the groups stack into one
            // continuous column.
            mb={5}
            transition={`margin-bottom ${COLLAPSE_DURATION}s ${COLLAPSE_EASE_CSS}`}
            css={GROUP_COLLAPSE}
            {...props}
        >
            {title && (
                // The mini rail collapses the label entirely — height and
                // trailing margin ease to zero so the groups stack into one
                // continuous column. 21px = the single body-3 line these
                // labels are (by design); a taller cap would defeat the
                // collapse.
                <SidebarGroupEl
                    opacity={1}
                    maxH='21px'
                    mb={1}
                    overflow='hidden'
                    // Bottom-anchored text: the box's top edge is fixed in
                    // flow, so a shrinking height clips at the BOTTOM by
                    // default — pinning the line to the bottom makes the
                    // text slide up under the top edge instead.
                    display='flex'
                    alignItems='flex-end'
                    transition={`${FADE_IN}, ${TITLE_COLLAPSE_TRANSITION}`}
                    css={TITLE_COLLAPSE}
                >
                    {title}
                </SidebarGroupEl>
            )}
            {children}
        </VStack>
    );
}

/**
 * The rail expand/collapse control — self-styled so it renders anywhere
 * (the rail footer's far edge today, via Sidebar.FooterControls `end`);
 * pass style props to position or hide it.
 */
function SidebarToggleButton(props: React.ComponentProps<typeof IconButton>) {
    const { isSidebarOpen, toggleSidebar } = useSidebar();
    return (
        <IconButton
            aria-label='Toggle sidebar'
            aria-expanded={isSidebarOpen}
            variant='ghost'
            onClick={toggleSidebar}
            // xs (32px): the rail's compact control size.
            size='xs'
            {...chromeControlStyles}
            // The ghost recipe tints an expanded control (its menu-trigger
            // idiom); here aria-expanded is the rail's state, not a popup's
            // — the open rail must not read as a pressed button.
            _expanded={{
                bg: 'transparent',
                color: chromeControlStyles.color,
                _hover: chromeControlStyles._hover,
            }}
            {...props}
        >
            <Icon
                as={
                    isSidebarOpen
                        ? BsLayoutSidebarInset
                        : BsLayoutSidebarInsetReverse
                }
                boxSize={4}
            />
        </IconButton>
    );
}

/** The mini rail's row tooltip: right-side, arrowed, disabled while the
 * label is visible. Always the same tree — a conditional wrapper would
 * remount the row on every rail toggle and kill the label's fade. */
function RailTooltip({
    content,
    children,
}: {
    content: string;
    children: React.ReactElement;
}) {
    const mini = useSidebarMini();
    return (
        <Tooltip
            content={content}
            positioning={{ placement: 'right' }}
            showArrow
            openDelay={200}
            closeDelay={0}
            disabled={!mini}
            lazyMount
            unmountOnExit
        >
            {children}
        </Tooltip>
    );
}

/** Collapsed = the icons-only mini rail (ONE model at every breakpoint):
 * labels/titles hide and rows grow tooltips. */
function useSidebarMini() {
    const { isSidebarOpen } = useSidebar();
    return !isSidebarOpen;
}

// The one row geometry every item kind shares: icon well, label. Compact
// 32px rows at every breakpoint (the one-rail model) — minH centers the
// label and a wrapping one can still grow the row. In the mini rail the
// label fades out (its box stays in flow — the nav's overflow-x clips it)
// and the untouched icon well centers in the rail.
function SidebarItemRow({
    icon,
    label,
    textStyle = 'body-3',
}: {
    icon: IconType;
    label: string;
    textStyle?: string;
}) {
    return (
        <Flex align='center' gap={2} minH={8}>
            <Center
                // 32px in BOTH states (an 8px icon inset — the same box as
                // the footer's icon buttons), so the icon never moves
                // horizontally during the collapse.
                w={8}
                flexShrink={0}
            >
                <Icon as={icon} boxSize={4} />
            </Center>
            <Text
                textStyle={textStyle}
                whiteSpace='nowrap'
                opacity={1}
                transition={FADE_IN}
                css={FADE_WITH_COLLAPSE}
            >
                {label}
            </Text>
        </Flex>
    );
}

interface ItemLinkProps {
    to: LinkProps['to'];
    label: string;
    icon: IconType;
    /** Mini-rail tooltip override — defaults to the label. Use it to
     * namespace rows when stacked contexts share the rail (e.g.
     * "Org Name / Page Name"). */
    tooltip?: string;
}

function SidebarItemLink({ to, label, icon, tooltip }: ItemLinkProps) {
    // isPending highlights the tapped item while its lazy chunk/loader
    // resolves.
    const anchor = (
        // outline:none only suppresses the UA ring on the raw anchor — the
        // recipe re-draws the themed focus-visible ring on the item pill.
        <NavLink to={to} style={{ textDecoration: 'none', outline: 'none' }}>
            {({ isActive, isPending }) => (
                <SidebarItemContent
                    isActive={isActive}
                    isPending={isPending}
                    label={label}
                    icon={icon}
                />
            )}
        </NavLink>
    );
    // The mini rail hides labels — the tooltip takes over. Always the same
    // tree (disabled when expanded): a conditional wrapper would REMOUNT the
    // row on every rail toggle and kill the label's fade mid-flight.
    return <RailTooltip content={tooltip ?? label}>{anchor}</RailTooltip>;
}

type SidebarItemContentProps = {
    isActive: boolean;
    isPending: boolean;
    label: string;
    icon: IconType;
};

// NavLink exposes `isActive` only through its children render prop, where
// hooks can't live — so the row body is its own component.
function SidebarItemContent({
    isActive,
    isPending,
    label,
    icon: IconComponent,
}: SidebarItemContentProps) {
    return (
        <SidebarItemEl
            data-active={isActive ? 'true' : undefined}
            data-pending={isPending ? 'true' : undefined}
        >
            <SidebarItemRow
                icon={IconComponent}
                label={label}
                textStyle={isActive || isPending ? 'body-2' : 'body-3'}
            />
        </SidebarItemEl>
    );
}

type SidebarNamespace = typeof SidebarRoot & {
    Provider: typeof SidebarProvider;
    useFlyout: typeof useSidebarFlyout;
    Brand: typeof SidebarBrand;
    Nav: typeof SidebarNav;
    ItemGroup: typeof SidebarItemGroup;
    ItemLink: typeof SidebarItemLink;
    ToggleButton: typeof SidebarToggleButton;
    FooterControls: typeof SidebarFooterControls;
    ContextRow: typeof SidebarContextRow;
    ContextTrigger: typeof SidebarContextTrigger;
    useContextTrail: typeof useSidebarContextTrail;
};

const Sidebar = Object.assign(SidebarRoot, {
    Provider: SidebarProvider,
    useFlyout: useSidebarFlyout,
    Brand: SidebarBrand,
    Nav: SidebarNav,
    ItemGroup: SidebarItemGroup,
    ItemLink: SidebarItemLink,
    ToggleButton: SidebarToggleButton,
    FooterControls: SidebarFooterControls,
    ContextRow: SidebarContextRow,
    ContextTrigger: SidebarContextTrigger,
    useContextTrail: useSidebarContextTrail,
}) as SidebarNamespace;

export { Sidebar, useSidebarMini };
